/**
 * Dialog para importar contatos do WhatsApp
 * 
 * Usa arquitetura multi-provider através do hook useProviderUtils.
 * Funciona com Z-API, Evolution e outros providers que implementem
 * a action 'import_contacts' no provider-proxy.
 * 
 * ⚠️ IMPORTANTE: Usa normalizeBrazilianPhone() para comparar telefones
 * e evitar duplicação causada pelo 9º dígito de celulares brasileiros.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Loader2, 
  Download, 
  Search, 
  Phone, 
  User, 
  Building2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useProviderUtils, WhatsAppContact } from '@/hooks/useProviderUtils';
import { toast } from 'sonner';
import { normalizeBrazilianPhone, createNormalizedPhoneSet } from '@/lib/phone-utils';

// Interface para inbox com status
interface Inbox {
  id: string;
  name: string;
  identifier: string;
  status: string;
  channel_type: string;
}

// Contato com estado de seleção e existência
interface ContactWithState extends WhatsAppContact {
  selected: boolean;
  alreadyExists: boolean;
  importing?: boolean;
}

interface ImportContactsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  existingPhones: string[]; // Telefones já cadastrados (normalizados)
  onSuccess: () => void;
}

export function ImportContactsDialog({
  open,
  onOpenChange,
  tenantId,
  existingPhones,
  onSuccess,
}: ImportContactsDialogProps) {
  const { importContacts, downloadAvatar } = useProviderUtils();
  
  // Estados
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [selectedInboxId, setSelectedInboxId] = useState<string>('');
  const [contacts, setContacts] = useState<ContactWithState[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingInboxes, setLoadingInboxes] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // Busca inboxes do tipo WhatsApp quando abre o dialog
  useEffect(() => {
    if (open && tenantId) {
      fetchInboxes();
    }
  }, [open, tenantId]);

  // Limpa estados quando fecha
  useEffect(() => {
    if (!open) {
      setContacts([]);
      setSelectedInboxId('');
      setSearchQuery('');
      setImportProgress(0);
    }
  }, [open]);

  // Busca inboxes WhatsApp ativas
  const fetchInboxes = async () => {
    try {
      setLoadingInboxes(true);
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, identifier, status, channel_type')
        .eq('tenant_id', tenantId)
        .eq('channel_type', 'whatsapp')
        .eq('status', 'active');

      if (error) throw error;
      setInboxes(data || []);
      
      // Auto-seleciona se só tem uma inbox
      if (data?.length === 1) {
        setSelectedInboxId(data[0].id);
      }
    } catch (error) {
      console.error('Erro ao buscar inboxes:', error);
      toast.error('Erro ao carregar caixas de entrada');
    } finally {
      setLoadingInboxes(false);
    }
  };

  // Busca contatos do WhatsApp via provider
  const handleFetchContacts = async () => {
    if (!selectedInboxId) {
      toast.error('Selecione uma caixa de entrada');
      return;
    }

    try {
      setLoadingContacts(true);
      setContacts([]);
      
      const whatsappContacts = await importContacts(selectedInboxId);
      
      /**
       * ═══════════════════════════════════════════════════════════════════════════════
       * ⚠️ COMPARAÇÃO DE TELEFONES - USAR SEMPRE normalizeBrazilianPhone()
       * 
       * NUNCA compare telefones apenas com .replace(/\D/g, '')!
       * Isso causa duplicação por causa do 9º dígito de celulares brasileiros.
       * 
       * Exemplo do problema:
       * - Banco: 5562991986300 (com 9)
       * - Z-API: 556291986300 (sem 9)
       * - Comparação simples: diferentes → duplica!
       * - Com normalização: ambos viram 5562991986300 → detecta existente!
       * ═══════════════════════════════════════════════════════════════════════════════
       */
      const normalizedExisting = createNormalizedPhoneSet(existingPhones);
      
      // Mapeia contatos com estado, usando normalização para detectar existentes
      const contactsWithState: ContactWithState[] = whatsappContacts.map(contact => ({
        ...contact,
        selected: false,
        // ⚠️ CRÍTICO: Usar normalizeBrazilianPhone() para comparar!
        alreadyExists: normalizedExisting.has(normalizeBrazilianPhone(contact.phone)),
      }));
      
      // Ordena: novos primeiro, depois existentes
      contactsWithState.sort((a, b) => {
        if (a.alreadyExists === b.alreadyExists) {
          return (a.name || '').localeCompare(b.name || '');
        }
        return a.alreadyExists ? 1 : -1;
      });
      
      setContacts(contactsWithState);
      
      const newCount = contactsWithState.filter(c => !c.alreadyExists).length;
      toast.success(`${contactsWithState.length} contatos encontrados (${newCount} novos)`);
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
      toast.error('Erro ao buscar contatos do WhatsApp');
    } finally {
      setLoadingContacts(false);
    }
  };

  // Filtra contatos pela busca
  const filteredContacts = useMemo(() => {
    if (!searchQuery) return contacts;
    const query = searchQuery.toLowerCase();
    return contacts.filter(c => 
      c.name?.toLowerCase().includes(query) || 
      c.phone.includes(query)
    );
  }, [contacts, searchQuery]);

  // Contadores
  const selectedCount = contacts.filter(c => c.selected && !c.alreadyExists).length;
  const newContactsCount = contacts.filter(c => !c.alreadyExists).length;

  // Toggle seleção individual
  const toggleContact = (phone: string) => {
    setContacts(prev => prev.map(c => 
      c.phone === phone && !c.alreadyExists
        ? { ...c, selected: !c.selected }
        : c
    ));
  };

  // Seleciona/deseleciona todos
  const toggleSelectAll = () => {
    const allSelected = contacts.filter(c => !c.alreadyExists).every(c => c.selected);
    setContacts(prev => prev.map(c => 
      c.alreadyExists ? c : { ...c, selected: !allSelected }
    ));
  };

  /**
   * Importa contatos selecionados para o banco de dados
   * 
   * Fluxo para cada contato:
   * 1. Insere contato no banco (sem avatar)
   * 2. Busca e baixa foto de perfil do WhatsApp
   * 3. Atualiza contato com o caminho do avatar
   */
  const handleImport = async () => {
    const toImport = contacts.filter(c => c.selected && !c.alreadyExists);
    
    if (toImport.length === 0) {
      toast.error('Selecione ao menos um contato para importar');
      return;
    }

    try {
      setImporting(true);
      setImportProgress(0);
      
      let imported = 0;
      let errors = 0;
      let withAvatar = 0;

      for (const contact of toImport) {
        try {
          // 1. Insere contato no banco (precisa do ID para salvar o avatar)
          const { data: insertedContact, error: insertError } = await supabase
            .from('contacts')
            .insert({
              tenant_id: tenantId,
              phone: contact.phone,
              name: contact.name,
              metadata: {
                imported_from: 'whatsapp',
                is_business: contact.isBusiness,
                imported_at: new Date().toISOString(),
              },
            })
            .select('id')
            .single();

          if (insertError) {
            // Ignora erro de duplicata
            if (!insertError.message.includes('duplicate')) {
              throw insertError;
            }
            // Se já existe, considera como importado mas sem atualizar avatar
            imported++;
            setImportProgress(Math.round(((imported + errors) / toImport.length) * 100));
            continue;
          }

          const contactId = insertedContact.id;
          
          // 2. Tenta baixar e salvar avatar (não bloqueia se falhar)
          try {
            const avatarPath = await downloadAvatar(selectedInboxId, contact.phone, contactId);
            
            if (avatarPath) {
              // 3. Atualiza contato com o caminho do avatar
              await supabase
                .from('contacts')
                .update({ avatar_url: avatarPath })
                .eq('id', contactId);
              
              withAvatar++;
            }
          } catch (avatarErr) {
            // Ignora erro de avatar - contato já foi criado
            console.log(`Avatar não disponível para ${contact.phone}:`, avatarErr);
          }
          
          imported++;
        } catch (err) {
          console.error('Erro ao importar contato:', contact.phone, err);
          errors++;
        }

        setImportProgress(Math.round(((imported + errors) / toImport.length) * 100));
      }

      // Mensagem de sucesso com info sobre avatares
      if (errors > 0) {
        toast.warning(`${imported} contatos importados (${withAvatar} com foto), ${errors} com erro`);
      } else if (withAvatar > 0) {
        toast.success(`${imported} contatos importados com sucesso! (${withAvatar} com foto)`);
      } else {
        toast.success(`${imported} contatos importados com sucesso!`);
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Erro na importação:', error);
      toast.error('Erro ao importar contatos');
    } finally {
      setImporting(false);
    }
  };

  // Formata telefone para exibição
  const formatPhone = (phone: string) => {
    if (phone.length === 13 && phone.startsWith('55')) {
      return `+${phone.slice(0, 2)} (${phone.slice(2, 4)}) ${phone.slice(4, 9)}-${phone.slice(9)}`;
    }
    return `+${phone}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Importar Contatos do WhatsApp
          </DialogTitle>
          <DialogDescription>
            Selecione uma caixa de entrada para buscar os contatos do WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 overflow-hidden">
          {/* Seletor de Inbox */}
          <div className="flex gap-2">
            <Select
              value={selectedInboxId}
              onValueChange={setSelectedInboxId}
              disabled={loadingInboxes || loadingContacts || importing}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Selecione a caixa de entrada" />
              </SelectTrigger>
              <SelectContent>
                {inboxes.map(inbox => (
                  <SelectItem key={inbox.id} value={inbox.id}>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{inbox.name}</span>
                      <span className="text-muted-foreground text-xs">
                        ({inbox.identifier})
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              onClick={handleFetchContacts}
              disabled={!selectedInboxId || loadingContacts || importing}
            >
              {loadingContacts ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span className="ml-2">Buscar</span>
            </Button>
          </div>

          {/* Mensagem quando não há inboxes */}
          {!loadingInboxes && inboxes.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Nenhuma caixa de entrada WhatsApp ativa encontrada.
              </p>
              <p className="text-muted-foreground text-sm">
                Configure uma inbox WhatsApp para importar contatos.
              </p>
            </div>
          )}

          {/* Lista de contatos */}
          {contacts.length > 0 && (
            <>
              {/* Busca e seleção */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nome ou telefone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={toggleSelectAll}
                  disabled={importing || newContactsCount === 0}
                >
                  {contacts.filter(c => !c.alreadyExists).every(c => c.selected) 
                    ? 'Desmarcar Todos' 
                    : 'Selecionar Todos'}
                </Button>
              </div>

              {/* Info de contatos */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{filteredContacts.length} contatos</span>
                <span>•</span>
                <span className="text-primary">{newContactsCount} novos</span>
                <span>•</span>
                <span>{contacts.length - newContactsCount} já cadastrados</span>
              </div>

              {/* Lista scrollável com altura fixa para scroll funcionar */}
              <ScrollArea className="border rounded-md h-[300px]">
                <div className="p-2 space-y-1">
                  {filteredContacts.map(contact => (
                    <div
                      key={contact.phone}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        contact.alreadyExists 
                          ? 'bg-muted/50 opacity-60' 
                          : contact.selected 
                            ? 'bg-primary/10' 
                            : 'hover:bg-accent'
                      }`}
                    >
                      <Checkbox
                        checked={contact.selected}
                        onCheckedChange={() => toggleContact(contact.phone)}
                        disabled={contact.alreadyExists || importing}
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {contact.isBusiness ? (
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <User className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="font-medium truncate">
                            {contact.name || 'Sem nome'}
                          </span>
                          {contact.isBusiness && (
                            <Badge variant="secondary" className="text-xs">
                              Empresa
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatPhone(contact.phone)}
                        </p>
                      </div>

                      {contact.alreadyExists && (
                        <Badge variant="outline" className="gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Já cadastrado
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Progress bar durante importação */}
              {importing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Importando contatos...</span>
                    <span>{importProgress}%</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Estado de carregamento */}
          {loadingContacts && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Buscando contatos do WhatsApp...</p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={importing}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleImport}
            disabled={selectedCount === 0 || importing}
          >
            {importing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Importar {selectedCount} contato{selectedCount !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}